import { onBeforeUnmount, onMounted, type Ref } from 'vue'

/**
 * Publishes the bounding rect of a `<main>` (or any element) as
 * CSS custom properties on `:root` so global CSS (the PrimeVue
 * Toast anchor in `main.scss`, future overlays) can position
 * themselves relative to the main content region without JS.
 *
 * Vars emitted (all in px, no unit suffix):
 *   --app-main-top
 *   --app-main-right
 *   --app-main-bottom
 *   --app-main-left
 *   --app-main-width
 *   --app-main-height
 *
 * Triggers an update on:
 *   - initial mount
 *   - any size change to `<main>` (via ResizeObserver — covers
 *     sidebar collapse / expand)
 *   - window resize
 *   - window scroll (in case of sticky chrome shifting top)
 *
 * Caller passes a template ref bound to the main element. The
 * tracker installs listeners in `onMounted` and tears them down
 * in `onBeforeUnmount` — single instance per layout, no leaks
 * across route changes.
 */
export function useMainBoundsTracker(mainRef: Ref<HTMLElement | null>): void {
  let observer: ResizeObserver | null = null

  const publish = (): void => {
    const el = mainRef.value
    if (!el) return
    const rect = el.getBoundingClientRect()
    const root = document.documentElement.style
    root.setProperty('--app-main-top', `${rect.top}px`)
    root.setProperty('--app-main-right', `${rect.right}px`)
    root.setProperty('--app-main-bottom', `${rect.bottom}px`)
    root.setProperty('--app-main-left', `${rect.left}px`)
    root.setProperty('--app-main-width', `${rect.width}px`)
    root.setProperty('--app-main-height', `${rect.height}px`)
  }

  const onResize = (): void => publish()
  const onScroll = (): void => publish()

  onMounted(() => {
    publish()
    if (mainRef.value && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(publish)
      observer.observe(mainRef.value)
    }
    window.addEventListener('resize', onResize, { passive: true })
    window.addEventListener('scroll', onScroll, { passive: true, capture: true })
  })

  onBeforeUnmount(() => {
    observer?.disconnect()
    observer = null
    window.removeEventListener('resize', onResize)
    window.removeEventListener('scroll', onScroll, { capture: true })
  })
}
